import { Button } from "@/components/ui/button";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { useState, useRef } from "react";
import { OptimizedImage } from "@/components/OptimizedImage";

export const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [isLocationsOpen, setIsLocationsOpen] = useState(false);
  const aboutTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const locationsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const handleAboutMouseEnter = () => {
    if (aboutTimeoutRef.current) {
      clearTimeout(aboutTimeoutRef.current);
    }
    setIsAboutOpen(true);
  };

  const handleAboutMouseLeave = () => {
    aboutTimeoutRef.current = setTimeout(() => {
      setIsAboutOpen(false);
    }, 150);
  };

  const handleLocationsMouseEnter = () => {
    if (locationsTimeoutRef.current) clearTimeout(locationsTimeoutRef.current);
    setIsLocationsOpen(true);
  };

  const handleLocationsMouseLeave = () => {
    locationsTimeoutRef.current = setTimeout(() => {
      setIsLocationsOpen(false);
    }, 150);
  };

  const handleIndustriesClick = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('industries');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <>
    <header className="fixed top-0 left-0 right-0 z-50 bg-background border-b border-border">

      {/* Utility Bar */}
      <div style={{ backgroundColor: 'hsl(350 70% 50%)' }}>
        <div className="container mx-auto px-6 py-2">
          <div className="flex items-center justify-center lg:justify-end gap-6 text-sm">
            <a 
              href="tel:+15712224765" 
              className="flex items-center gap-2 text-white/90 hover:text-white transition-colors font-semibold"
              aria-label="Call us at 571-222-4765"
            >
              <Phone className="h-4 w-4" />
              <span>(571) 222-4765</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="bg-background/80 backdrop-blur-md">
        <div className="container mx-auto px-6 py-0">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="animate-fade-in hover:opacity-80 transition-opacity">
            <div className="w-[220px] h-[90px] md:w-[300px] md:h-[115px] flex items-center">
              <OptimizedImage
                src="/lovable-uploads/hh-logo-stacked-new.webp"
                alt="Hollins & Hur logo"
                className="w-full h-full object-contain"
                width={220}
                height={85}
                priority
                sizes="(max-width: 768px) 220px, 300px"
              />
            </div>
          </Link>

          {/* Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <div 
              className="relative"
              onMouseEnter={handleAboutMouseEnter}
              onMouseLeave={handleAboutMouseLeave}
            >
              <button 
                className={`flex items-center gap-1 hover:text-primary transition-colors font-medium bg-transparent border-none cursor-pointer ${location.pathname === '/about' || location.pathname === '/our-team' ? 'text-primary' : 'text-foreground'}`}
              >
                About
                <ChevronDown className={`h-4 w-4 transition-transform ${isAboutOpen ? 'rotate-180' : ''}`} />
              </button>
              {isAboutOpen && (
                <div className="absolute top-full left-0 mt-2 w-48 bg-background border border-border rounded-md shadow-lg z-50 p-1">
                  <Link 
                    to="/about" 
                    className="block px-4 py-2 text-foreground hover:bg-muted hover:text-primary transition-colors rounded-md"
                    onClick={() => setIsAboutOpen(false)}
                  >
                    Our Firm & Values
                  </Link>
                </div>
              )}
            </div>
            <Link to="/services" className={`hover:text-primary transition-colors font-medium ${location.pathname === '/services' ? 'text-primary' : 'text-foreground'}`}>
              Services
            </Link>
            <button 
              onClick={handleIndustriesClick} 
              className={`hover:text-primary transition-colors font-medium bg-transparent border-none cursor-pointer ${location.pathname === '/' ? 'text-primary' : 'text-foreground'}`}
            >
              Who We Serve
            </button>
            <div 
              className="relative"
              onMouseEnter={handleLocationsMouseEnter}
              onMouseLeave={handleLocationsMouseLeave}
            >
              <button 
                className={`flex items-center gap-1 hover:text-primary transition-colors font-medium bg-transparent border-none cursor-pointer ${['/cpa-arlington-va', '/cpa-fairfax-va', '/accountant-washington-dc', '/cpa-alexandria-va', '/cpa-reston-va'].includes(location.pathname) ? 'text-primary' : 'text-foreground'}`}
              >
                Areas We Serve
                <ChevronDown className={`h-4 w-4 transition-transform ${isLocationsOpen ? 'rotate-180' : ''}`} />
              </button>
              {isLocationsOpen && (
                <div className="absolute top-full left-0 mt-2 w-52 bg-background border border-border rounded-md shadow-lg z-50 p-1">
                  <Link to="/cpa-arlington-va" className="block px-4 py-2 text-foreground hover:bg-muted hover:text-primary transition-colors rounded-md" onClick={() => setIsLocationsOpen(false)}>Arlington, VA</Link>
                  <Link to="/cpa-fairfax-va" className="block px-4 py-2 text-foreground hover:bg-muted hover:text-primary transition-colors rounded-md" onClick={() => setIsLocationsOpen(false)}>Fairfax, VA</Link>
                  <Link to="/accountant-washington-dc" className="block px-4 py-2 text-foreground hover:bg-muted hover:text-primary transition-colors rounded-md" onClick={() => setIsLocationsOpen(false)}>Washington, DC</Link>
                  <Link to="/cpa-alexandria-va" className="block px-4 py-2 text-foreground hover:bg-muted hover:text-primary transition-colors rounded-md" onClick={() => setIsLocationsOpen(false)}>Alexandria, VA</Link>
                  <Link to="/cpa-reston-va" className="block px-4 py-2 text-foreground hover:bg-muted hover:text-primary transition-colors rounded-md" onClick={() => setIsLocationsOpen(false)}>Reston, VA</Link>
                </div>
              )}
            </div>
          </nav>

          {/* CTA Button */}
          <div className="flex items-center space-x-4">
            {/* Mobile menu button */}
            <Button 
              variant="ghost" 
              size="sm"
              className="lg:hidden"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
            
            <Link to="/consultation">
              <Button 
                variant="hero" 
                size="sm" 
                className="hidden lg:inline-flex"
              >
                Book A Free Discovery Call
              </Button>
            </Link>
            <a 
              href="https://www.securefirmportal.com/Account/Login/9694"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button 
                variant="outline-primary"
                size="sm"
                className="hidden lg:inline-flex"
                aria-label="Access client portal"
              >
                Client Portal
              </Button>
            </a>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-border">
            <nav className="flex flex-col space-y-4 mt-4">
              <div className="space-y-2">
                <p className="text-muted-foreground text-sm font-medium">About</p>
                <Link 
                  to="/about" 
                  className={`block pl-3 hover:text-primary transition-colors ${location.pathname === '/about' ? 'text-primary' : 'text-foreground'}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Our Firm & Values
                </Link>
              </div>
              <Link to="/services" className={`hover:text-primary transition-colors font-medium ${location.pathname === '/services' ? 'text-primary' : 'text-foreground'}`}>
                Services
              </Link>
              <button 
                onClick={handleIndustriesClick} 
                className={`hover:text-primary transition-colors font-medium bg-transparent border-none cursor-pointer text-left ${location.pathname === '/' ? 'text-primary' : 'text-foreground'}`}
              >
                Who We Serve
              </button>
              <div className="space-y-2">
                <p className="text-muted-foreground text-sm font-medium">Areas We Serve</p>
                {[
                  { to: "/cpa-arlington-va", label: "Arlington, VA" },
                  { to: "/cpa-fairfax-va", label: "Fairfax, VA" },
                  { to: "/accountant-washington-dc", label: "Washington, DC" },
                  { to: "/cpa-alexandria-va", label: "Alexandria, VA" },
                  { to: "/cpa-reston-va", label: "Reston, VA" },
                ].map((city) => (
                  <Link
                    key={city.to}
                    to={city.to}
                    className={`block pl-3 hover:text-primary transition-colors ${location.pathname === city.to ? 'text-primary' : 'text-foreground'}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {city.label}
                  </Link>
                ))}
              </div>
              <div className="flex flex-col space-y-3 pt-4">
                <Link to="/consultation">
                  <Button 
                    variant="hero" 
                    size="sm"
                  >
                    Book A Free Discovery Call
                  </Button>
                </Link>
                <a 
                  href="https://www.securefirmportal.com/Account/Login/9694"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    aria-label="Access client portal"
                  >
                    Client Portal
                  </Button>
                </a>
              </div>
            </nav>
          </div>
        )}
      </div>
      </div>
    </header>
    <div aria-hidden="true" className="h-[var(--header-height)]" />
    </>
  );
};

